import type {
  ApplicationTestingActivity,
  ApplicationTestingActivityType,
} from '@/core/models/applicationTesting.models';
import { Icon } from '@/kit/Icon';
import './ApplicationTestingTimeline.scss';

const ICON: Record<ApplicationTestingActivityType, { icon: string; tone: string }> = {
  Created: { icon: 'add_circle_outline', tone: 'green' },
  StatusChanged: { icon: 'swap_horiz', tone: 'blue' },
  PriorityChanged: { icon: 'flag', tone: 'amber' },
  TagsChanged: { icon: 'sell', tone: 'purple' },
  CommentAdded: { icon: 'chat_bubble_outline', tone: 'teal' },
};

const splitTags = (value: string | null): string[] =>
  (value ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

/** "20 Oct 2026, 14:05" in the viewer's time zone. */
export function formatActivityTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}

/** What happened, as a sentence (and the detail lines under it). */
export function describeActivity(activity: ApplicationTestingActivity): { title: string; detail?: string } {
  switch (activity.type) {
    case 'Created':
      return { title: 'Ticket created' };
    case 'StatusChanged':
      return { title: `Status changed from ${activity.oldValue || '—'} to ${activity.newValue || '—'}` };
    case 'PriorityChanged':
      return { title: `Priority changed from ${activity.oldValue || '—'} to ${activity.newValue || '—'}` };
    case 'TagsChanged': {
      const before = splitTags(activity.oldValue);
      const after = splitTags(activity.newValue);
      const added = after.filter((tag) => !before.includes(tag));
      const removed = before.filter((tag) => !after.includes(tag));
      const parts = [
        added.length ? `added ${added.join(', ')}` : '',
        removed.length ? `removed ${removed.join(', ')}` : '',
      ]
        .filter(Boolean)
        .join('; ');
      return { title: 'Tags updated', detail: parts || undefined };
    }
    case 'CommentAdded':
      return { title: 'Comment added', detail: activity.newValue ?? undefined };
    default:
      return { title: String(activity.type) };
  }
}

/** The ticket's history, newest first: one entry per status, priority, tag and comment change. */
export function ApplicationTestingTimeline({ activity }: { activity: ApplicationTestingActivity[] | null }) {
  if (activity === null) return <p className="application-testing-timeline-empty">Loading history…</p>;
  if (activity.length === 0) return <p className="application-testing-timeline-empty">No activity yet.</p>;

  return (
    <ol className="application-testing-timeline" aria-label="Ticket history">
      {activity.map((item) => {
        const { icon, tone } = ICON[item.type] ?? ICON.Created;
        const { title, detail } = describeActivity(item);
        return (
          <li key={item.id} className="application-testing-timeline-item">
            <span className={`application-testing-timeline-dot ${tone}`}>
              <Icon name={icon} outlined />
            </span>
            <div className="application-testing-timeline-body">
              <p className="application-testing-timeline-title">{title}</p>
              {detail && <p className="application-testing-timeline-detail">{detail}</p>}
              <p className="application-testing-timeline-meta">
                {item.actorName ? `${item.actorName} · ` : ''}
                {formatActivityTime(item.createdAt)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
